import { useState, useCallback, useMemo, Fragment } from 'react';
import { useIntl } from 'react-intl';

import map from 'lodash/map';
import capitalize from 'lodash/capitalize';

import Grid from '@mui/material/Grid2';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';

import SearchFilterBar, {
  QuickFiltersType,
} from 'src/components/SearchFilterBar';

import {
  useGetCancellations,
  useGetCancellationKindOptions,
  useGetCancellationReasonOptions,
} from 'src/pages/CancellationsPage/api';
import {
  useUpdateCancellationNotices,
  useUpdateWithdrawCancellationNotices,
} from 'src/pages/ProjectDetailsPage/api';
import JobLink from 'src/components/JobLink';
import DataTable from 'src/components/DataTable';
import { forceString } from 'src/utils/datetime';
import PageLayout from 'src/components/PageLayout';
import ProjectLink from 'src/components/ProjectLink';
import { PencilAdornment } from 'src/utils/components';
import { resolveClearFilters } from 'src/utils/request';

import messages from './messages';
import { AdvancedFiltersState } from 'src/components/AdvancedFilters';
import AdvancedFiltersButton from 'src/components/AdvancedFiltersButton';
import { CancellationFilters } from './components/CancellationFilters';
import { CancellationData } from '../ProjectDetailsPage/components/JobCalendar';
import { REJECT_REASON } from '../ProjectDetailsPage/api/queries/useGetRejectReason';
import CancellationBackupButton from './components/CancellationBackupButton/CancellationBackupButton';
import CreateCancellationFormModal from './components/CreateCancellationFormModal/CreateCancellationFormModal';
import CancellationModal from '../ProjectDetailsPage/components/JobDetails/components/CancellationModal/CancellationModal';

type CancellationType = 'all' | 'open' | 'transfer';

enum StateType {
  PROCESSED = 'processed',
}

const navTabs: Array<{ value: CancellationType; label: string }> = [
  {
    value: 'all',
    label: 'All',
  },
  {
    value: 'open',
    label: 'Open',
  },
  {
    value: 'transfer',
    label: 'Transfer',
  },
];

export default function CancellationsPage() {
  const { formatMessage } = useIntl();

  const [state, setState] = useState<
    Record<string, string | boolean | string[]>
  >({
    key: navTabs[0].value,
    status: StateType.PROCESSED,
  });
  const [filters, setFilters] = useState<Record<string, string>>({});

  const [openAdvancedFilter, setOpenAdvancedFilter] = useState<boolean>(false);
  const [advancedFilters, setAdvancedFilters] = useState<
    AdvancedFiltersState[]
  >([]);
  const [openCancellation, setOpenCancellation] = useState(false);
  const [editCancellation, setEditCancellation] =
    useState<UCM.CancellationType | null>(null);

  const [params, setParams] = useState<Record<string, string | number>>({
    page: 1,
  });

  const { data: cancellations, isFetching: isFetchingCancellations } =
    useGetCancellations({
      filters: resolveClearFilters({
        ...state,
        ...filters,
      }),
      dynamicFilters: advancedFilters,
      params,
    });

  const { mutate: updateCancellationNotices } = useUpdateCancellationNotices({
    inCancellationsPage: true,
  });

  const { data: cancellationKindOptions } = useGetCancellationKindOptions();
  const { data: cancellationReasonOptions } = useGetCancellationReasonOptions();

  const { mutate: withdrawCancellationNotices } =
    useUpdateWithdrawCancellationNotices({
      inCancellationsPage: true,
    });

  const handleEditCancellation = useCallback(
    (cancellation: UCM.CancellationType) => {
      setEditCancellation(cancellation);
      setOpenCancellation(true);
    },
    [],
  );

  const fixedColumns = useMemo(() => {
    return [
      {
        id: 'student',
        label: formatMessage(messages.studentColumnLabel),
        render: (_: unknown, item: UCM.CancellationType) => {
          if (!item?.user) return '-';
          const { id, firstName, lastName } = item.user;
          return (
            <Typography variant="inherit">{`${id} - ${firstName} ${lastName}`}</Typography>
          );
        },
      },
      {
        id: 'id',
        label: formatMessage(messages.cancellationIdColumnLabel),
        render: (id: string) => <Typography variant="inherit">{id}</Typography>,
        align: 'center',
      },
      {
        id: 'created',
        label: formatMessage(messages.createdByColumnLabel),
        render: (_: unknown, item: UCM.CancellationType) => {
          return (
            <>
              <Typography variant="inherit">
                {capitalize(item.source)}
              </Typography>
              <Typography variant="inherit">{item.createdAt}</Typography>
            </>
          );
        },
        align: 'center',
      },
      {
        id: 'jobId',
        label: formatMessage(messages.jobIdColumnLabel),
        render: (jobId: string, { project }: UCM.CancellationType) => (
          <JobLink jobId={jobId} projectUuid={project.uuid} target="_blank" />
        ),
        align: 'center',
      },
      {
        id: 'project',
        label: formatMessage(messages.projectDetailsColumnLabel),
        render: (_: unknown, item: UCM.CancellationType) => {
          if (!item?.project) return '-';
          const { name, id, projectManager, uuid } = item.project;
          return (
            <>
              <ProjectLink projectUuid={uuid} label={id} />
              <Typography variant="inherit">{name}</Typography>
              <Typography variant="inherit">({projectManager})</Typography>
            </>
          );
        },
        align: 'center',
      },
      {
        id: 'shifts',
        label: formatMessage(messages.shiftsColumnLabel),
        render: (_: unknown, item: UCM.CancellationType) => {
          const { shiftDates, shiftCount } = item;
          return (
            <Tooltip
              title={
                <Fragment>
                  {formatMessage(messages.cancelledShiftsTooltip)}
                  <br />
                  {map(shiftDates, (date) => (
                    <Fragment key={date}>
                      <span>• {forceString(date)}</span>
                      <br />
                    </Fragment>
                  ))}
                </Fragment>
              }
            >
              <Typography variant="inherit">
                {`${shiftDates.length}/${shiftCount}`}
              </Typography>
            </Tooltip>
          );
        },
        align: 'center',
      },
      {
        id: 'reason',
        label: formatMessage(messages.reasonColumnLabel),
        render: (_: unknown, item: UCM.CancellationType) => (
          <Typography variant="inherit">{item.reason.title}</Typography>
        ),
        align: 'center',
      },
      {
        id: 'explanation',
        align: 'center',
        maxWidth: '15vw',
        label: formatMessage(messages.explanationColumnLabel),
        render: (explanation: string | null) => (
          <Typography variant="inherit">{explanation ?? '-'}</Typography>
        ),
      },
      {
        id: 'adminComment',
        align: 'center',
        maxWidth: '15vw',
        label: formatMessage(messages.adminCommentColumnLabel),
        render: (adminComment: string | null) => (
          <Typography variant="inherit">{adminComment || '-'}</Typography>
        ),
      },
    ];
  }, []);

  const allColumns = useMemo(() => {
    return [
      ...fixedColumns,
      {
        id: 'backup',
        label: '',
        align: 'center',
        render: (_: unknown, item: UCM.CancellationType) => (
          <IconButton onClick={() => handleEditCancellation(item)}>
            <PencilAdornment />
          </IconButton>
        ),
      },
    ];
  }, [fixedColumns]);

  const openColumns = useMemo(() => {
    return [
      ...fixedColumns,
      {
        id: 'actions',
        label: formatMessage(messages.backupButtonLabel),
        align: 'center',
        width: 0,
        render: (_: unknown, item: UCM.CancellationType) => (
          <Grid container spacing={1} justifyContent="center">
            <CancellationBackupButton uuid={item.uuid} isBackupRequired />
            <CancellationBackupButton
              uuid={item.uuid}
              isBackupRequired={false}
            />
          </Grid>
        ),
      },
      {
        id: 'edit',
        label: '',
        align: 'center',
        width: 0,
        render: (_: unknown, item: UCM.CancellationType) => (
          <IconButton
            color="inherit"
            size="small"
            onClick={() => handleEditCancellation(item)}
          >
            <PencilAdornment />
          </IconButton>
        ),
      },
    ];
  }, [fixedColumns]);

  const columns: Record<
    CancellationType,
    Array<{
      id: string;
      label: string | React.ReactNode;
    }>
  > = useMemo(
    () => ({
      all: allColumns,
      open: openColumns,
      transfer: allColumns,
    }),
    [allColumns, openColumns],
  );

  const searchFilters = useMemo(() => {
    return [
      {
        id: 'user_name',
        type: 'text' as QuickFiltersType['type'],
        placeholder: formatMessage(messages.searchUserNamePlaceholder),
      },
      {
        id: 'user_id',
        type: 'text' as QuickFiltersType['type'],
        placeholder: formatMessage(messages.searchUidPlaceholder),
      },
      {
        id: 'project_id',
        type: 'text' as QuickFiltersType['type'],
        placeholder: formatMessage(messages.projectIdPlaceholder),
      },
      {
        id: 'job_id',
        type: 'text' as QuickFiltersType['type'],
        placeholder: formatMessage(messages.searchJobIdPlaceholder),
      },
      {
        id: 'cancellation_id',
        type: 'text' as QuickFiltersType['type'],
        placeholder: formatMessage(messages.searchCancelIdPlaceholder),
      },
    ];
  }, []);

  const openFilters = useMemo(() => {
    const openOptions: Record<string, string[]> = {};
    const backupable = cancellationKindOptions?.filter(
      (option) => option.backupable,
    );
    const reasonFor = cancellationReasonOptions?.filter(
      (option) => !option.actionable,
    );

    if (backupable?.length) {
      openOptions.kind = backupable.map((option) => option.label);
    }

    if (reasonFor?.length) {
      openOptions.reason = reasonFor.map((option) => option.value);
    }

    return openOptions;
  }, [cancellationKindOptions, cancellationReasonOptions]);

  const handleActiveTabChange = useCallback(
    (tab: CancellationType) => {
      let newState = { key: tab, status: StateType.PROCESSED } as Record<
        string,
        string | boolean | string[]
      >;
      if (tab === 'open') {
        newState = { ...newState, ...openFilters };
      }
      setState(newState);
      setParams({ page: 1 });
    },
    [cancellationKindOptions],
  );

  const handlePagination = useCallback((_: unknown, page: number) => {
    setParams({ page: page + 1 });
  }, []);

  // eslint-disable-next-line
  const handleSearch = useCallback((search: Record<string, any>) => {
    setParams({ page: 1 });
    setFilters(search);
  }, []);

  const handleCancellation = useCallback(async (data: CancellationData) => {
    const { type, cancellationUuid, ...payload } = data;
    const filteredData = Object.fromEntries(
      Object.entries(payload).filter(
        ([_, value]) => value != null && value !== '',
      ),
    );
    return new Promise<boolean>((resolve) => {
      const mutationOptions = {
        onSuccess: () => {
          closeCancellation();
          resolve(true);
        },
        onError: () => resolve(false),
      };

      if (type === 'update') {
        void updateCancellationNotices(
          {
            cancellationUuid,
            data: filteredData,
          },
          mutationOptions,
        );
      } else if (type === 'withdraw') {
        void withdrawCancellationNotices(
          {
            cancellationUuid,
            reason_code: payload.reason_code,
          },
          mutationOptions,
        );
      }
    });
  }, []);

  const closeCancellation = useCallback(() => {
    setOpenCancellation(false);
    setEditCancellation(null);
  }, []);

  const handleAdvancedFilters = useCallback(() => {
    setOpenAdvancedFilter(!openAdvancedFilter);
  }, [openAdvancedFilter]);

  const handleAdvancedFiltersSearch = useCallback(
    (filters: AdvancedFiltersState[]) => {
      setParams({ page: 1 });
      setAdvancedFilters(filters);
      setOpenAdvancedFilter(false);
    },
    [],
  );

  return (
    <PageLayout
      iconName="cancellations"
      title={formatMessage(messages.title)}
      tabs={navTabs}
      activeTab={state.key as CancellationType}
      onActiveTabChange={handleActiveTabChange}
    >
      <Grid container spacing={2} alignItems="center">
        <Grid size={6}>
          <SearchFilterBar
            fields={searchFilters}
            onSearch={handleSearch}
            variant="noBorder"
          />
        </Grid>
        <Grid size={1}>
          <AdvancedFiltersButton
            open={openAdvancedFilter}
            filters={advancedFilters}
            handleOpenFilters={handleAdvancedFilters}
          />
        </Grid>
        <Grid size={5} container alignItems="center" justifyContent="flex-end">
          <CreateCancellationFormModal />
        </Grid>
        <CancellationFilters
          open={openAdvancedFilter}
          handleSearch={handleAdvancedFiltersSearch}
          filters={advancedFilters}
        />
        <Grid size={12}>
          <DataTable
            rows={cancellations?.data}
            pagination={cancellations?.meta}
            columns={columns[state.key as CancellationType]}
            onPaginate={handlePagination}
            loading={isFetchingCancellations}
          />
        </Grid>
      </Grid>
      <CancellationModal
        type={REJECT_REASON.RESET_TIME_TRACKING}
        enabledFetch={openCancellation}
        onConfirm={handleCancellation}
        open={openCancellation}
        onClose={closeCancellation}
        candidate={editCancellation?.user}
        cancellation={editCancellation}
      />
    </PageLayout>
  );
}
